"use client";

import React from "react";
import { ArrowRight, BookOpen, Clock, FileSpreadsheet } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardFooter, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
const caseStudiesData = [{
  id: "cs-1",
  industry: "Enterprise SaaS",
  format: "Case Study #084",
  readTime: "12 min read",
  title: "Scaling multi-tenant architecture under continuous enterprise load",
  challenge: "How a global platform restructured distributed databases and caching layers to support 400% transaction surge without SLA latency penalties.",
  framework: "Distributed Sharding & Async Query Pipeline",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1789806/img/case_study_saas_architecture.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1789806/img/case_study_saas_architecture.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1789806/img/case_study_saas_architecture.png-wvc-sizes",
  alt: "Enterprise software infrastructure and server analytics visualization"
}, {
  id: "cs-2",
  industry: "Healthcare Diagnostics",
  format: "Case Study #085",
  readTime: "15 min read",
  title: "Accelerating diagnostic validation through federated clinical trials",
  challenge: "Standardizing disparate multi-institutional imaging data while maintaining HIPAA compliance and reducing regulatory review timelines by half.",
  framework: "Federated Protocol & Multi-Center Validation",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1789806/img/case_study_healthcare_diagnostics.webp",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1789806/img/case_study_healthcare_diagnostics.webp-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1789806/img/case_study_healthcare_diagnostics.webp-wvc-sizes",
  alt: "Clinical laboratory diagnostic research and analytical review"
}, {
  id: "cs-3",
  industry: "FinTech RevOps",
  format: "Case Study #086",
  readTime: "10 min read",
  title: "Rebuilding billing reconciliation for cross-border currency flows",
  challenge: "Eliminating ledger discrepancies across 28 banking partners with automated exception routing and real-time settlement telemetry.",
  framework: "Event-Driven Ledger Reconciliation Framework",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1789806/img/case_study_fintech_revops.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1789806/img/case_study_fintech_revops.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1789806/img/case_study_fintech_revops.png-wvc-sizes",
  alt: "Financial operations and settlement data telemetry screen"
}];
export default function CaseStudies() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.12
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="case-study-list" data-nav="light" className="bg-background text-foreground py-20 sm:py-24 border-b border-border">
      <div className="max-w-[1280px] mx-auto px-5 sm:px-8 lg:px-12">
        {/* Masthead rule and section header */}
        <div className="mb-12 sm:mb-16">
          <div className="flex items-center gap-3 mb-4">
            <span className="h-px bg-foreground w-8 sm:w-12 inline-block" />
            <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground font-sans">
              Methodology & Practice
            </span>
          </div>

          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 pb-6 border-b border-border">
            <div>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-semibold tracking-tight text-foreground">
                Selected operational studies
              </h2>
              <p className="mt-3 text-base sm:text-lg text-muted-foreground max-w-2xl font-sans leading-relaxed">
                Examine how market leaders address architectural scale, diagnostic precision, and revenue operations.
              </p>
            </div>

            <div className="flex items-center gap-2 text-xs font-mono text-muted-foreground self-start md:self-end">
              <BookOpen className="size-4 text-muted-foreground" />
              <span>Peer-reviewed frameworks</span>
            </div>
          </div>
        </div>

        {/* 3 Case Study Cards Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.1
      }} className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-8">
          {caseStudiesData.map((study, index) => <motion.div key={study.id} data-index={index} variants={itemVariants} className="flex">
              <Card className="flex flex-col w-full h-full bg-card border border-border rounded-md overflow-hidden shadow-xs hover:shadow-md transition-[border-color,box-shadow] duration-300 hover:border-border/80">
                {/* Visual Cover Header */}
                <div className="relative aspect-[16/10] w-full overflow-hidden bg-muted border-b border-border">
                  <img src={study.image} data-wvc-srcset={study.srcset} data-wvc-sizes={study.sizes} alt={study.alt} loading="lazy" decoding="async" className="w-full h-full object-cover grayscale contrast-105 hover:grayscale-0 transition-[filter] duration-500" />
                  <div className="absolute top-3 left-3 flex gap-2">
                    <Badge variant="secondary" className="bg-background/95 backdrop-blur-sm text-foreground text-[11px] font-mono border-border rounded-xs px-2 py-0.5">
                      {study.format}
                    </Badge>
                  </div>
                </div>

                <CardHeader className="p-6 pb-3 space-y-3">
                  <div className="flex items-center justify-between gap-2">
                    <span className="inline-block text-xs font-semibold uppercase tracking-wider text-muted-foreground font-sans">
                      {study.industry}
                    </span>
                    <div className="flex items-center gap-1.5 text-xs text-muted-foreground font-mono">
                      <Clock className="size-3.5" />
                      <span>{study.readTime}</span>
                    </div>
                  </div>

                  <h3 className="text-xl font-serif font-semibold text-foreground leading-snug tracking-tight line-clamp-2">
                    {study.title}
                  </h3>
                </CardHeader>

                <CardContent className="p-6 pt-0 flex-1 flex flex-col justify-between space-y-4">
                  <p className="text-sm text-muted-foreground font-sans leading-relaxed">
                    {study.challenge}
                  </p>

                  <div className="pt-3 border-t border-border/60">
                    <div className="flex items-start gap-2 text-xs text-foreground/80 font-mono">
                      <FileSpreadsheet className="size-4 shrink-0 mt-0.5 text-muted-foreground" />
                      <span className="line-clamp-1">{study.framework}</span>
                    </div>
                  </div>
                </CardContent>

                <CardFooter className="p-6 pt-0">
                  <Button asChild variant="outline" className="w-full justify-between rounded-sm border-border text-foreground hover:bg-secondary hover:text-secondary-foreground font-medium text-xs tracking-normal transition-colors">
                    <Link to="/CaseStudiesPage" className="inline-flex items-center w-full justify-between">
                      <span>Explore framework</span>
                      <ArrowRight className="size-3.5 ml-2" />
                    </Link>
                  </Button>
                </CardFooter>
              </Card>
            </motion.div>)}
        </motion.div>

        {/* Footnote Strip */}
        <div className="mt-14 pt-6 border-t border-border flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-muted-foreground font-sans">
          <p>
            All case studies anonymize proprietary telemetry while preserving authentic structural frameworks.
          </p>
          <Button asChild variant="link" className="p-0 h-auto text-xs text-foreground font-medium underline underline-offset-4 hover:text-muted-foreground">
            <Link to="/ResearchReportsPage">
              Browse full research library &rarr;
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}